import React, { useCallback, useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import "./CommonTabs.scss"; // Import the CSS file for styling
import CommonList from "../CommonList/CommonList";
import Feature from "../../FeatureFlags/Feature/Feature";
import SearchResults from "../../../pages/SearchResults/SearchResults";
import {
  COMMON_MESSAGES,
  RECORDS_PER_PAGE_SEARCH_RESULTS,
  SEARCH_RESULTS_ATTRIBUTES,
  TABB_TITLE_EVENT_KEYS,
  VIRTUAL_CLASSROOMS_AND_ENGINES_API_PARAMS,
  SORT_COLUMN,
  SORT_ORDER
} from "../../../../helper/constants";
import { VIRTUAL_CLASS_ROOM_OR_ENGINES_API_PATH } from "../../../../helper/APIPaths";
import { useSelector } from "react-redux";
import CircularLoader from "../../../common/Loader/CircularLoader";
import { useTranslation } from "react-i18next";
import { isPilot } from "../../../../helper/commonFunctions";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";

interface TabOption {
  _source: any;
}

interface CommonTabsProps {
  tabs: any[]; // Array of tab titles
  category: string; // Optional category prop
}

const toTitleCase = (id: any): string => {
  const str = String(id ?? "").trim();
  if (!str) return str;
  return str
    .toLowerCase()
    .replace(/(^\s*\S|[-_\s]\S)/g, (match) => match.toUpperCase());
};

const CommonTabs: React.FC<CommonTabsProps> = ({ tabs, category }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<number>(0); // state to manage the active tab index
  const [tabContent, setTabContent] = useState<any>({}); // state to manage the content of each tab
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { userInformation } = useSelector((state: any) => state?.userInformation);
  const { subCategoryNameVirtualClassrooms, subCategoryNameEngines, itemName } = useSelector((state: any) => state?.virtualClassRoomsOrEngines);
  const [currentPage, setCurrentPage] = useState(1);
  const [isTitleLinkEnabled, setIsTitleLinkEnabled] = useState(true);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
    }
  }, [loading, activeTab]);

  useEffect(() => {
    if ((tabs && tabs.length > 0) || activeTab) {
      setLoading(true);
      fetchSelectedTabContent(activeTab); // Fetch content for the initially active tab
    }
  }, [tabs, activeTab]);

  const fetchSelectedTabContent = useCallback(async (currentTab: any, pageNo?: number) => {
    try {
      const {
        pilot_category_name_VC,
        pilot_category_name_EN,
        pilot_item_name_engines,
      } = VIRTUAL_CLASSROOMS_AND_ENGINES_API_PARAMS;

      const payload = {
        groupBy: tabs[currentTab]?.id,
        role: isPilot(userInformation?.jobTitle || ""),
        pageable: {
          pageNo: pageNo ? pageNo : currentPage,
          pageSize: RECORDS_PER_PAGE_SEARCH_RESULTS,
          sortBy: SORT_COLUMN,
          sortDir: SORT_ORDER
        },
      };

      let customPayload = {};

      if (category == pilot_category_name_VC) {
        customPayload = {
          ...payload,
          pilot_category_name: pilot_category_name_VC,
          pilot_subcategory_name: subCategoryNameVirtualClassrooms || null,
          pilot_item_name: itemName,
        };
      } else {
        customPayload = {
          ...payload,
          pilot_category_name: pilot_category_name_EN,
          pilot_item_name: subCategoryNameEngines,
        };
      }
      const response = await fetch(VIRTUAL_CLASS_ROOM_OR_ENGINES_API_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customPayload),
      });
      const res = await response.json();
      const data = res?.data;
      if (data) {
        setTabContent(data);
        setLoading(false);
        setError(false);
      }
    } catch (ierror) {
      console.error(
        "Error while fetching virtual classrooms data details",
        ierror
      );
      setLoading(false);
      setError(true);
    }
  }, []);

  const handleTabChange = (eventKey: number) => {
    setCurrentPage(1);
    setActiveTab(eventKey);
  };

  const onPageSelected = (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    fetchSelectedTabContent(activeTab, page); // Fetch content for the selected tab when the page changes
  };

  return (
    <Box className="cls-aircraftTabs tabsOptions">
      {/* Tabs Header */}
      <Tabs
        id="common-tabs-for-preflight"
        activeKey={activeTab}
        onSelect={(eventKey, event) => handleTabChange(Number(eventKey))}
        aria-label="common tabs"
        className="mb-3"
      >
        {tabs?.map((item, index) => {
          const eventKey = TABB_TITLE_EVENT_KEYS[item?.id.toLowerCase()] || item?.id;
          return (
            <Tab
              key={item.id + index}
              eventKey={index}
              title={
                <Tooltip
                  title={toTitleCase(item?.id)}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#384348",
                        fontFamily: "PP Telegraf",
                        fontWeight: "400",
                        color: "#FFFFFF",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#384348",
                      },
                    },
                  }}
                >
                  <span>{`${eventKey} (${item?.value})`}</span>
                </Tooltip>
              }
              className="cls-hometab"
            />
          );
        })}
      </Tabs>

      {/* Tabs Content */}
      {loading && <CircularLoader />}
      {tabContent && !loading && !error && (
        <Box className="tabContent">
          <Feature name="Search Results">
            <SearchResults
              dataSearch={tabContent}
              load={false}
              showMore={true}
              attributes={SEARCH_RESULTS_ATTRIBUTES}
              Watchlistflag={true}
              className="ViewcollectionContainer TopmetaData"
              onPageSelected={onPageSelected}
              currentPage={currentPage}
              paginator={true}
              isTitleLink={isTitleLinkEnabled}
            />
          </Feature>
        </Box>
      )}
      {!loading && !tabContent && (
        <div className="error">{t(COMMON_MESSAGES.NO_RECORDS_FOUND)}</div>
      )}
      {error && (
        <div className="error">{t(COMMON_MESSAGES.NO_DATA_AVAILABLE)}</div>
      )}
    </Box>
  );
};

export default CommonTabs;